import { clsx } from 'clsx';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import {
  alignLeft,
  alignRight,
  dropDown,
  icon,
  option,
  optionDefault,
  optionSelected,
  visible,
} from './dropDown.css';
import { Chip, ChipProps } from '../chip/chip';
import { sprinkles } from '@design-system/sprinkles.css';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Typography } from '../typography/typography';

type OptionValue = string | number;

type Option = {
  label: string;
  value: OptionValue;
  startIcon?: IconProp;
  endIcon?: IconProp;
  endCta?: ReactNode;
};

type DropDownProps = Omit<ChipProps, 'onClick'> & {
  options: Array<Option>;
  onClick: (value: OptionValue) => void;
  selectedValue?: OptionValue;
  alightRight?: boolean;
  menuClassName?: string;
  chipClassName?: string;
};

export const DropDown = (p: DropDownProps) => {
  const [open, setOpen] = useState(false);
  const chipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const click = function (ev: MouseEvent) {
      ev.target !== chipRef.current && setOpen(false);
    };
    document.addEventListener('click', click);
    return () => document.removeEventListener('click', click);
  }, []);

  return (
    <div
      className={clsx(
        sprinkles({ position: 'relative', maxWidth: 'fit-content' }),
      )}
    >
      <Chip
        {...p}
        endIcon={faCaretDown}
        selected={open}
        onClick={() => setOpen(true)}
        ref={chipRef}
        className={clsx(p?.chipClassName)}
      />
      <div
        className={clsx(
          dropDown,
          open && visible,
          p?.alightRight ? alignRight : alignLeft,
          p?.menuClassName,
        )}
        data-testid="dropDownMenu"
      >
        {p.options.map(({ label, value, startIcon, endIcon, endCta }) => (
          <div
            key={value}
            className={clsx(
              option,
              p?.selectedValue === value ? optionSelected : optionDefault,
            )}
            onClick={() => p?.onClick && p.onClick(value)}
          >
            {startIcon ? (
              <FontAwesomeIcon icon={startIcon} className={icon} />
            ) : null}
            <Typography
              variant="body"
              size="large"
              as="span"
              className={clsx(sprinkles({ width: '100%' }))}
            >
              {label}
            </Typography>
            {endIcon ? (
              <FontAwesomeIcon icon={endIcon} className={icon} />
            ) : null}
            {endCta ? endCta : null}
          </div>
        ))}
      </div>
    </div>
  );
};
